import React, { useEffect, useMemo } from 'react';
import {
  GroupLayout,
  JsonSchema,
  LayoutProps,
  RankedTester,
  rankWith,
  uiTypeIs,
  withIncreasedRank,
} from '@jsonforms/core';
import { JsonFormsDispatch, withJsonFormsLayoutProps } from '@jsonforms/react';
import { MantineLabelableLayoutRendererProps, MantineLayoutRenderer } from '../utils';
import { useNavigationContext } from '../../contexts/NavigationContext';
import { getDepth, isTopLevel, isRequired } from '../../utils/navigation';
import CollapsableSection from '../complex/CollapsableSection';
import { Box, Tabs } from '@mantine/core';
import { useIsSelected } from '../../hooks/useIsSelected';

export const groupTester: RankedTester = rankWith(1, uiTypeIs('Group'));

const GroupComponent = React.memo(function GroupComponent({
  visible,
  enabled,
  uischema,
  label,
  elements,
  required,
  selected,
  ...props
}: MantineLabelableLayoutRendererProps & { required: boolean; selected: boolean }) {
  //If group layout rendered by this renderer has more than one group (object) as a child, those children will be rendered as tabs.
  //Since some oneOfs are also of "object" type in schema, they will also be rendered as tabs and be returned from function below as groups.
  const { groups, others, labelsAndNames } = useMemo(
    () => mapLabelsGroupsAndOthers(elements, props.schema),
    [elements, props.schema]
  );
  const showTabs = groups.length > 1;

  return (
    <CollapsableSection
      label={label}
      path={props.path}
      required={required}
      selected={selected}
      description={props.schema?.description}
    >
      <MantineLayoutRenderer {...props} visible={visible} enabled={enabled} elements={others} />

      {showTabs ? (
        <Tabs defaultValue='0'>
          <Tabs.List mb='xs'>
            {labelsAndNames.map(({ label, name }, index) => (
              <GroupTab key={index} label={label} path={props.path + '.' + name} tabNumber={index} />
            ))}
          </Tabs.List>

          {groups.map((element, index) => (
            <Tabs.Panel key={index} value={index.toString()}>
              <JsonFormsDispatch
                uischema={element}
                schema={props.schema}
                path={props.path}
                enabled={enabled}
                renderers={props.renderers}
                cells={props.cells}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      ) : (
        <MantineLayoutRenderer {...props} visible={visible} enabled={enabled} elements={groups} />
      )}
    </CollapsableSection>
  );
});

const GroupTab = ({ label, path, tabNumber }: { label: string; path: string; tabNumber: number }) => {
  const { isSelected } = useIsSelected(path);
  return (
    <Tabs.Tab component={Box} key={tabNumber} value={tabNumber.toString()}>
      {label}{' '}
      <Box display='inline-block' w='1ch'>
        {isSelected ? '✓' : '⭘'}
      </Box>
    </Tabs.Tab>
  );
};

export const MantineGroupLayoutRenderer = ({
  uischema,
  schema,
  path,
  visible,
  enabled,
  renderers,
  cells,
  direction,
  label,
}: LayoutProps) => {
  const { isSelected } = useIsSelected(path);
  const { selectedTopNavigation } = useNavigationContext();

  const depth = getDepth(path);
  const topLevel = isTopLevel(depth);
  const required = isRequired(topLevel, path);
  const selected = required ? true : isSelected;
  const { elements } = uischema as GroupLayout;

  if (!visible) {
    return null;
  }
  if (topLevel && selectedTopNavigation !== path) {
    return null;
  }

  return (
    <GroupComponent
      elements={elements}
      schema={schema}
      path={path}
      direction={direction}
      visible={visible}
      enabled={enabled}
      uischema={uischema}
      renderers={renderers}
      cells={cells}
      label={label}
      required={required}
      selected={selected}
    />
  );
};

export default withJsonFormsLayoutProps(MantineGroupLayoutRenderer);

export const mantineGroupTester: RankedTester = withIncreasedRank(2, groupTester);

//For group layouts and some oneOfs (all paths of type "object") it returns labels and names (subpaths). For others it returns null.
//Function bellow (mapLabelsGroupsAndOthers) relies on result of this function to separate groups from basic input paths and to get groups' names and labels.
const getLabelAndNameIfGroup = (element: any, schema?: JsonSchema) => {
  const name = element.scope?.split('/').pop();
  if (!name) {
    return null;
  }
  const elementInSchema = schema?.properties?.[name];
  if (!elementInSchema || elementInSchema.type !== 'object') {
    return null;
  }
  return { label: elementInSchema?.title || 'unknown setting name', name }; //Must be truthy otherwise will not be treated as group in a code above.
};

//This function is used to separate groups (paths of type "object" - group layouts and some oneOfs) from other elements. It also returns labels and names for elements returned in "groups" array.
const mapLabelsGroupsAndOthers = (groupLayout: GroupLayout['elements'], schema?: JsonSchema) => {
  const groups: GroupLayout['elements'] = [];
  const others: GroupLayout['elements'] = [];
  const labelsAndNames: { label: string; name: string }[] = [];

  for (let i = 0; i < groupLayout.length; i++) {
    const element = groupLayout[i];
    const labelAndName = getLabelAndNameIfGroup(element, schema);
    if (labelAndName) {
      groups.push(element);
      labelsAndNames.push(labelAndName);
    } else {
      others.push(element);
    }
  }

  return { groups, others, labelsAndNames };
};
